import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingAppsSidepanel from 'benchling-apps/dashboard/BenchlingAppsSidepanel';
import BenchlingConfig from 'BenchlingConfig';
import * as NavConstants from 'constants/NavConstants';
import ConnectionSidepanel from 'instruments/connections/connection-sidepanel/ConnectionSidepanel';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as Colors from 'ui-library/Colors';
import SectionTabs from 'ui-library/SectionTabs';
import * as logging from 'util/Logging';

import MCPSidepanel from '../mcps/MCPSidepanel';
import TFISidepanel, {TFISidepanelInterface} from '../tfi/TFISidepanel';

interface Props {
  tfi: TFISidepanelInterface;
  activeSection: NavConstants.Section;
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
}

export default function ConnectionsSidepanel(props: Props): JSX.Element {
  const tabs = [];
  if (isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})) {
    tabs.push({
      handleClick: () => {
        SidepanelActions.openSection(NavConstants.Section.CONNECTIONS);
      },
      ariaControls: `${NavConstants.Section.CONNECTIONS}_PANEL`,
      id: `${NavConstants.Section.CONNECTIONS}_TAB`,
      isActive: props.activeSection === NavConstants.Section.CONNECTIONS,
      text: 'Connections',
    });
  }
  if (isMcpAiConnectorsFeatureEnabled()) {
    tabs.push({
      handleClick: () => {
        SidepanelActions.openSection(NavConstants.Section.MCPS);
      },
      ariaControls: `${NavConstants.Section.MCPS}_PANEL`,
      id: `${NavConstants.Section.MCPS}_TAB`,
      isActive: props.activeSection === NavConstants.Section.MCPS,
      text: MCP_PRODUCT_NAME_PLURAL,
    });
  }
  tabs.push({
    handleClick: () => {
      SidepanelActions.openSection(NavConstants.Section.TFI);
    },
    ariaControls: `${NavConstants.Section.TFI}_PANEL`,
    id: `${NavConstants.Section.TFI}_TAB`,
    isActive: props.activeSection === NavConstants.Section.TFI,
    text: 'External Files',
  });
  if (BenchlingConfig.ENABLE_BENCHLING_APPS) {
    tabs.push({
      handleClick: () => {
        SidepanelActions.openSection(NavConstants.Section.BENCHLING_APPS);
      },
      ariaControls: `${NavConstants.Section.BENCHLING_APPS}_PANEL`,
      id: `${NavConstants.Section.BENCHLING_APPS}_TAB`,
      isActive: props.activeSection === NavConstants.Section.BENCHLING_APPS,
      text: 'Apps',
    });
  }
  // Do not show Apps option while end user page disabled
  const connectionsTab =
    tabs.length > 1 ? <SectionTabs className={css(styles.sectionTabs)} tabs={tabs} /> : <></>;
  switch (props.activeSection) {
    case NavConstants.Section.BENCHLING_APPS:
      return (
        <BenchlingAppsSidepanel
          isCompactDisplay={props.isCompactDisplay}
          setIsCompactDisplay={props.setIsCompactDisplay}
          isExpanded={props.isExpanded}
          activeItemId={props.activeItemId}
          connectionsTab={connectionsTab}
        />
      );
    case NavConstants.Section.CONNECTIONS:
      return (
        <ConnectionSidepanel
          isCompactDisplay={props.isCompactDisplay}
          setIsCompactDisplay={props.setIsCompactDisplay}
          isExpanded={props.isExpanded}
          activeItemId={props.activeItemId}
          connectionsTab={connectionsTab}
        />
      );
    case NavConstants.Section.MCPS:
      if (!isMcpAiConnectorsFeatureEnabled()) {
        // If feature flag is disabled, fall back to default (TFI)
        logging.error(
          `User is trying to access ${MCP_PRODUCT_NAME_PLURAL} sidepanel but feature flag is disabled`
        );
        return <TFISidepanel {...props.tfi} connectionsTab={connectionsTab} />;
      }
      return (
        <MCPSidepanel
          isCompactDisplay={props.isCompactDisplay}
          setIsCompactDisplay={props.setIsCompactDisplay}
          isExpanded={props.isExpanded}
          activeItemId={props.activeItemId}
          connectionsTab={connectionsTab}
        />
      );
    default:
      return <TFISidepanel {...props.tfi} connectionsTab={connectionsTab} />;
  }
}
const styles = StyleSheet.create({
  sectionTabs: {
    borderBottomColor: Colors.GRAY,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    margin: '0 -12px',
  },
});
